import {
  ENTITY_ACCESS_RIGHTS,
  HTTP_STATUS_CODE,
  QUERY_STATUS,
} from "constants/constants";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  createBillingClientQuery,
  deleteBillingClientQuery,
  getBillingClientByIdQuery,
  updateBillingClientQuery,
  getBillingCompaniesBySlug,
  getBillingClientByIdArrayQuery,
  searchBillingClientByNameQuery,
} from "./queries";
import _ from "lodash";
import { useUserContext } from "api/user/UserProvider";
import { canUserPerformAction } from "access-roles/general-functions";
import { BILLING_CLIENT_ROLES } from "access-roles/roles";
import { extractApiFieldErrorsFlat } from "utils/apiError";

const BillingClientContext = createContext({});

export const useBillingClientContext = () => useContext(BillingClientContext);

export const BillingClientProvider = ({ ...props }) => {
  const [billingClientById, setBillingClientById] = useState({});
  const [createdBillingClient, setCreatedBillingClient] = useState({});
  const [createdBillingClientStatus, setCreatedBillingClientStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [editBillingClientStatus, setEditBillingClientStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [displayBillingClientStatus, setDisplayBillingClientStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [deleteBillingClientStatus, setDeleteBillingClientStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [billingClientBySlug, setBillingClientBySlug] = useState({});
  const [billingClientsByName, setBillingClientsByName] = useState([]);
  const [billingClientsByIds, setBillingClientsByIds] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [billingClientAccessRights, setBillingClientAccessRights] = useState(
    []
  );
  const { userScope, userRoleList, setApiErrorResponse } = useUserContext();

  const getBillingClientBySlug = useCallback(async (slug) => {
    setBillingClientAccessRights([]);
    try {
      const result = await getBillingCompaniesBySlug({ slug, userScope });
      const hasGetAccessRights = result?.access.includes(
        ENTITY_ACCESS_RIGHTS.GET
      );
      if (hasGetAccessRights) {
        setBillingClientBySlug(result);
        setBillingClientAccessRights(result?.access);
        setDisplayBillingClientStatus(QUERY_STATUS.SUCCESS);
      }
    } catch (e) {
      if (e.response?.status === HTTP_STATUS_CODE.NOT_FOUND) {
        setDisplayBillingClientStatus(QUERY_STATUS.NOT_FOUND);
      } else {
        setDisplayBillingClientStatus(QUERY_STATUS.ERROR);
      }
      console.error("getBillingCompaniesBySlug", e);
      throw e;
    }
  }, [userScope]);

  const getBillingClientByIdArray = useCallback(
    async (ids) => {
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: BILLING_CLIENT_ROLES.ROLE_GET_BILLING_CLIENT,
        })
      ) {
        return;
      }
      try {
        setBillingClientsByIds(await getBillingClientByIdArrayQuery({ ids, userScope }));
      } catch (e) {
        console.error("getBillingClientByIdArray", e);
        throw e;
      }
    },
    [userScope, userRoleList]
  );

  const createNewBillingClient = useCallback(async (data) => {
    setApiErrorResponse(null);
    try {
      setCreatedBillingClientStatus(QUERY_STATUS.LOADING);
      const result = await createBillingClientQuery({ data, userScope });
      if (result) {
        setCreatedBillingClientStatus(QUERY_STATUS.SUCCESS);
        setCreatedBillingClient(result);
      }
    } catch (e) {
      setCreatedBillingClientStatus(QUERY_STATUS.ERROR);
      const errors = e.response?.data?.["bad-request"];
      if (errors) {
        setApiErrorResponse(extractApiFieldErrorsFlat(errors));
      }
      console.error("createBillingClient", e);
      throw e;
    }
  }, [userScope]);

  const getBillingClientById = useCallback(async (id) => {
    try {
      const result = await getBillingClientByIdQuery({ id, userScope });
      setBillingClientById(result);
      setBillingClientAccessRights(result?.access);
      setDisplayBillingClientStatus(QUERY_STATUS.SUCCESS);
    } catch (e) {
      setDisplayBillingClientStatus(QUERY_STATUS.ERROR);
      console.error("getBillingClientById", e);
      throw e;
    }
  }, [userScope]);

  const updateBillingClient = useCallback(async (id, data) => {
    setApiErrorResponse(null);
    try {
      setEditBillingClientStatus(QUERY_STATUS.LOADING);
      setBillingClientById(await updateBillingClientQuery({ id, data, userScope }));
      setEditBillingClientStatus(QUERY_STATUS.SUCCESS);
      setRefresh(true);
    } catch (e) {
      setEditBillingClientStatus(QUERY_STATUS.ERROR);
      const errors = e.response?.data?.["bad-request"];
      if (errors) {
        setApiErrorResponse(extractApiFieldErrorsFlat(errors));
      }
      console.error("updateBillingClient", e);
      throw e;
    }
  }, [userScope]);

  const deleteBillingClient = useCallback(async (id) => {
    setDeleteBillingClientStatus(QUERY_STATUS.LOADING);
    try {
      await deleteBillingClientQuery({ id, userScope });
      setDeleteBillingClientStatus(QUERY_STATUS.SUCCESS);
      setBillingClientById({});
      setBillingClientBySlug({});
    } catch (e) {
      setDeleteBillingClientStatus(QUERY_STATUS.ERROR);
      console.error("deleteBillingClient", e);
      throw e;
    }
  }, [userScope]);

  const searchBillingClientByName = useCallback(
    async (search) => {
      try {
        const result = await searchBillingClientByNameQuery({ search, userScope });
        setBillingClientsByName((old) =>
          _.uniqWith([...old, ...result], _.isEqual)
        );
      } catch (e) {
        console.error("getClientsByName", e);
        throw e;
      }
    },
    [userScope]
  );

  const values = useMemo(
    () => ({
      billingClientBySlug,
      getBillingClientBySlug,
      billingClientById,
      createdBillingClientStatus,
      createdBillingClient,
      getBillingClientById,
      createNewBillingClient,
      updateBillingClient,
      editBillingClientStatus,
      setEditBillingClientStatus,
      deleteBillingClient,
      setCreatedBillingClientStatus,
      displayBillingClientStatus,
      billingClientsByName,
      getBillingClientByIdArray,
      billingClientsByIds,
      refresh,
      setRefresh,
      billingClientAccessRights,
      setBillingClientAccessRights,
      searchBillingClientByName,
      userScope,
      deleteBillingClientStatus,
      setDeleteBillingClientStatus,
    }),
    [
      refresh,
      setRefresh,
      billingClientBySlug,
      billingClientById,
      createdBillingClient,
      createdBillingClientStatus,
      editBillingClientStatus,
      displayBillingClientStatus,
      billingClientsByName,
      billingClientsByIds,
      billingClientAccessRights,
      setBillingClientAccessRights,
      userScope,
      deleteBillingClientStatus,
      setDeleteBillingClientStatus,
    ]
  );
  return <BillingClientContext.Provider value={values} {...props} />;
};
